import { Instagram, Mail, MapPin, Phone, Twitter } from "lucide-react";
import Image from "next/image";

export default function Footer() {
  return (
    <footer className="bg-background/60 motion-safe:animate-in motion-safe:fade-in-0 relative isolate w-full border-t backdrop-blur-sm motion-safe:duration-700">
      {/* Fancy gradient background - continues from contact bottom left, bottom center, and bottom right */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* Base gradient - starts with contact's ending colors at top left, top center, and top right */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 100% 80% at top left, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at top center, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at top right, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at left center, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at right center, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom left, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom center, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom right, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              linear-gradient(to bottom right, oklch(0.646 0.222 41.116 / 0.15) 0%, transparent 40%, oklch(0.769 0.188 70.08 / 0.1) 100%),
              oklch(1 0 0)
            `,
          }}
        />
        {/* Animated floating orbs */}
        <div className="bg-chart-1/20 absolute top-20 -left-32 size-96 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse" />
        <div className="bg-chart-4/20 absolute -right-32 bottom-20 size-80 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-1000" />
        <div className="bg-chart-2/15 absolute top-1/2 left-1/2 size-72 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-2000" />

        {/* Subtle mesh gradient overlay */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "linear-gradient(to bottom right, transparent 0%, oklch(0.646 0.222 41.116 / 0.05) 30%, oklch(0.828 0.189 84.429 / 0.05) 70%, transparent 100%)",
          }}
        />
      </div>

      <div className="container mx-auto px-4 py-8 sm:px-6 sm:py-12 lg:px-8 lg:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* Brand Section */}
          <div className="space-y-4 sm:col-span-2 lg:col-span-1">
            <Image
              src="/Logo_black.png"
              alt="GD Group Studio logo"
              className="h-16 w-auto object-contain sm:h-20"
              loading="lazy"
              width={200}
              height={100}
              sizes="(max-width: 640px) 64px, 80px"
            />
            <p
              className="text-xs sm:text-sm"
              style={{
                backgroundImage:
                  "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Capturing moments, telling stories.
            </p>
            <p className="text-muted-foreground text-xs leading-relaxed sm:text-sm">
              Fine-art and lifestyle photography blending natural light with cinematic tones.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3
              className="text-sm font-semibold sm:text-base"
              style={{
                backgroundImage:
                  "linear-gradient(to right, oklch(0.145 0 0), oklch(0.646 0.222 41.116))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Quick Links
            </h3>
            <nav className="flex flex-col gap-2.5">
              {[
                { href: "#portfolio", label: "Portfolio" },
                { href: "#services", label: "Services" },
                { href: "#about", label: "About" },
                { href: "#contact", label: "Contact" },
              ].map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring group flex items-center gap-2 text-xs transition-all duration-300 hover:translate-x-1 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:text-sm"
                >
                  <span
                    className="from-chart-1 to-chart-4 h-0.5 w-0 bg-linear-to-r transition-all duration-300 group-hover:w-2"
                    style={{
                      background:
                        "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
                    }}
                  />
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div className="space-y-4">
            <h3
              className="text-sm font-semibold sm:text-base"
              style={{
                backgroundImage:
                  "linear-gradient(to right, oklch(0.145 0 0), oklch(0.646 0.222 41.116))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Contact
            </h3>
            <div className="space-y-2.5 text-xs sm:text-sm">
              <a
                href="mailto:gdgroupstudio@gmail.com"
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring group flex items-center gap-2 transition-all duration-300 hover:translate-x-1 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
              >
                <Mail className="size-4 opacity-70 transition-transform duration-300 group-hover:scale-110" />
                gdgroupstudio@gmail.com
              </a>
              <div className="space-y-1">
                <a
                  href="tel:+919936701147"
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring group flex items-center gap-2 transition-all duration-300 hover:translate-x-1 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                >
                  <Phone className="size-4 opacity-70 transition-transform duration-300 group-hover:scale-110" />
                  +91 99367 01147
                </a>
                <a
                  href="tel:+917572022775"
                  className="text-muted-foreground hover:text-foreground focus-visible:ring-ring group flex items-center gap-2 transition-all duration-300 hover:translate-x-1 focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                >
                  <Phone className="size-4 opacity-70 transition-transform duration-300 group-hover:scale-110" />
                  +91 75720 22775
                </a>
              </div>
              <address className="text-muted-foreground group flex items-start gap-2 leading-relaxed not-italic transition-all duration-300 hover:translate-x-1">
                <MapPin className="mt-0.5 size-4 shrink-0 opacity-70 transition-transform duration-300 group-hover:scale-110" />
                <span>
                  Gadaiya Lonipar, Naribari
                  <br />
                  Prayagraj, Uttar Pradesh 212106
                  <br />
                  India
                </span>
              </address>
            </div>
          </div>

          {/* Social Media */}
          <div className="space-y-4">
            <h3
              className="text-sm font-semibold sm:text-base"
              style={{
                backgroundImage:
                  "linear-gradient(to right, oklch(0.145 0 0), oklch(0.646 0.222 41.116))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Follow Us
            </h3>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href="https://www.instagram.com/gdgroupstudio/"
                aria-label="Instagram"
                className="bg-background/80 hover:bg-background focus-visible:ring-ring group relative flex size-10 items-center justify-center rounded-full border transition-all duration-300 hover:scale-110 hover:shadow-md focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:size-11"
                title="Instagram"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  borderColor: "oklch(0.646 0.222 41.116 / 0.2)",
                }}
              >
                <Instagram className="size-5 text-[#E1306C] transition-transform duration-300 group-hover:scale-110" />
              </a>
              <a
                href="#"
                aria-label="Twitter"
                className="bg-background/80 hover:bg-background focus-visible:ring-ring group relative flex size-10 items-center justify-center rounded-full border transition-all duration-300 hover:scale-110 hover:shadow-md focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:size-11"
                title="Twitter"
                style={{
                  borderColor: "oklch(0.828 0.189 84.429 / 0.2)",
                }}
              >
                <Twitter className="size-5 text-[#1DA1F2] transition-transform duration-300 group-hover:scale-110" />
              </a>
              <a
                href="#"
                aria-label="Behance"
                className="bg-background/80 hover:bg-background focus-visible:ring-ring group relative flex size-10 items-center justify-center rounded-full border transition-all duration-300 hover:scale-110 hover:shadow-md focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:size-11"
                title="Behance"
                style={{
                  borderColor: "oklch(0.6 0.118 184.704 / 0.2)",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-5 text-[#1769FF] transition-transform duration-300 group-hover:scale-110"
                >
                  <path d="M3 7h5.2c.9 0 1.6.2 2.1.6.5.4.7 1 .7 1.8 0 .6-.2 1.1-.6 1.5-.2.2-.5.4-.8.5.5.2.9.5 1.1.8.3.4.4.9.4 1.5 0 .9-.3 1.6-.8 2.1S9.5 17 8.5 17H3V7zm4.8 4c.4 0 .7-.1.9-.3.2-.2.3-.5.3-.8 0-.7-.4-1-1.2-1H5.2v2.1h2.6zM7.9 15c.8 0 1.2-.4 1.2-1.1s-.4-1.1-1.2-1.1H5.2V15h2.7zM14.8 10.6c.2-.7.6-1.2 1.1-1.6.5-.4 1.3-.6 2.2-.6.9 0 1.7.2 2.2.6.5.4.9 1 1 1.7h-2c-.1-.2-.2-.4-.3-.5-.2-.2-.5-.3-.9-.3-.5 0-.9.2-1.2.5-.3.4-.5.9-.5 1.5 0 .7.2 1.2.5 1.6.3.4.8.5 1.3.5.6 0 1.1-.2 1.4-.6.2-.2.3-.5.3-.8h-1.7v-1.4h3.7V15h-1.2l-.2-.7c-.2.3-.6.5-1 .7-.4.2-.9.3-1.4.3-1.1 0-2-.3-2.6-1-.7-.6-1-1.6-1-2.8 0-.6.1-1.2.2-1.7zM14 7h5v1.2h-5V7z" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="text-muted-foreground border-border/50 mt-8 flex flex-col items-center justify-between gap-4 border-t pt-6 text-xs sm:mt-12 sm:flex-row sm:pt-8 sm:text-sm">
          <p className="text-center sm:text-left">
            © {new Date().getFullYear()} GD Group Studio. All rights reserved.
          </p>
          <p className="text-center text-xs sm:text-sm">
            Made with{" "}
            <span
              className="font-semibold"
              style={{
                backgroundImage:
                  "linear-gradient(to right, oklch(0.646 0.222 41.116), oklch(0.828 0.189 84.429))",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              ❤️
            </span>{" "}
            in India
          </p>
        </div>
      </div>
    </footer>
  );
}
